import {
    type EngagementChatAttachment,
    type EngagementChatMessagesPage,
    type EngagementChatPostedMessage,
    type EngagementChatUnseenCount,
    engagementChatAttachmentSchema,
    engagementChatMessagesPageSchema,
    engagementChatPostedMessageSchema,
    engagementChatUnseenCountSchema,
    resolveEngagementChatAttachmentMimeType,
} from '@houzy/schemas/partner-cockpit/chat';

import { apiRequest } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';

export type PostChatMessageInput = {
    text: string;
    attachmentUuids?: string[];
};

export async function fetchChatMessages(
    engagementUuid: string,
    cursor: string | undefined,
    signal?: AbortSignal,
): Promise<EngagementChatMessagesPage> {
    const query = cursor ? `?cursor=${cursor}` : '';
    return apiRequest(partnerCockpitApiPath(`engagements/${engagementUuid}/chat${query}`), {
        signal,
        responseSchema: engagementChatMessagesPageSchema,
    });
}

export async function fetchChatUnseenCount(
    engagementUuid: string,
    lastSeenMessageUuid: string,
    signal?: AbortSignal,
): Promise<EngagementChatUnseenCount> {
    return apiRequest(
        partnerCockpitApiPath(
            `engagements/${engagementUuid}/chat/unseen-count?lastSeenMessageUuid=${lastSeenMessageUuid}`,
        ),
        { signal, responseSchema: engagementChatUnseenCountSchema },
    );
}

export function plainTextToDelta(text: string): { ops: { insert: string }[] } {
    return { ops: [{ insert: text.endsWith('\n') ? text : `${text}\n` }] };
}

function fileForUpload(file: File): File {
    const resolvedMimeType = resolveEngagementChatAttachmentMimeType({
        name: file.name,
        type: file.type,
    });
    if (resolvedMimeType.length === 0 || resolvedMimeType === file.type) {
        return file;
    }
    return new File([file], file.name, { type: resolvedMimeType, lastModified: file.lastModified });
}

export async function uploadChatAttachment(
    engagementUuid: string,
    file: File,
    signal?: AbortSignal,
): Promise<EngagementChatAttachment> {
    const formData = new FormData();
    formData.append('file', fileForUpload(file));
    return apiRequest(partnerCockpitApiPath(`engagements/${engagementUuid}/chat/attachments`), {
        method: 'POST',
        body: formData,
        signal,
        responseSchema: engagementChatAttachmentSchema,
    });
}

export async function removeChatAttachment(
    engagementUuid: string,
    attachmentUuid: string,
    signal?: AbortSignal,
): Promise<void> {
    return apiRequest(partnerCockpitApiPath(`engagements/${engagementUuid}/chat/attachments/${attachmentUuid}`), {
        method: 'DELETE',
        signal,
    });
}

export async function postChatMessage(
    engagementUuid: string,
    { text, attachmentUuids }: PostChatMessageInput,
    signal?: AbortSignal,
): Promise<EngagementChatPostedMessage> {
    const trimmedText = text.trim();
    const body: { content?: ReturnType<typeof plainTextToDelta>; attachmentUuids?: string[] } = {};
    if (trimmedText.length > 0) {
        body.content = plainTextToDelta(trimmedText);
    }
    if (attachmentUuids !== undefined && attachmentUuids.length > 0) {
        body.attachmentUuids = attachmentUuids;
    }
    return apiRequest(partnerCockpitApiPath(`engagements/${engagementUuid}/chat`), {
        method: 'POST',
        body,
        signal,
        responseSchema: engagementChatPostedMessageSchema,
    });
}
